import { Theme } from '@mui/material';
import {
    DesignSystemAlertColorScheme,
    DesignSystemAlertSize,
    DesignSystemAlertStacking,
    DesignSystemColorModeFlattened,
    DesignSystemIconSizing,
} from '../../theme/design-system/types';

type AlertSizeInput = {
    size: DesignSystemAlertSize;
};

type AlertSizeOutput = {
    paddingX: number;
    paddingY: number;
    iconSize: DesignSystemIconSizing;
};

export const alertSizeValues: Record<DesignSystemAlertSize, AlertSizeOutput> = {
    xs: {
        paddingX: 1.5,
        paddingY: 1.5,
        iconSize: 'sm',
    },
    sm: {
        paddingX: 2,
        paddingY: 2,
        iconSize: 'md',
    },
    md: {
        paddingX: 2,
        paddingY: 2,
        iconSize: 'md',
    },
};

export const getSizeStyles = ({ size }: AlertSizeInput): AlertSizeOutput => {
    return alertSizeValues[size];
};

type AlertColorInput = {
    theme: Theme;
    colorScheme: DesignSystemAlertColorScheme;
};

type AlertColorOutput = {
    iconColor: DesignSystemColorModeFlattened;
    backgroundColor: string;
};

export const getColorStyles = ({
    theme,
    colorScheme,
}: AlertColorInput): AlertColorOutput => {
    switch (colorScheme) {
        case 'neutral': {
            return {
                backgroundColor: theme.palette.ds.bg.neutral.tertiary,
                iconColor: 'ds.fg.neutral.secondary',
            };
        }
        case 'accent1': {
            return {
                backgroundColor: theme.palette.ds.bg.accent1.tertiary,
                iconColor: 'ds.fg.accent1.secondary',
            };
        }
        case 'accent2': {
            return {
                backgroundColor: theme.palette.ds.bg.accent2.tertiary,
                iconColor: 'ds.fg.accent2.secondary',
            };
        }
        case 'critical': {
            return {
                backgroundColor: theme.palette.ds.bg.critical.tertiary,
                iconColor: 'ds.fg.critical.secondary',
            };
        }
        case 'attention': {
            return {
                backgroundColor: theme.palette.ds.bg.attention.tertiary,
                iconColor: 'ds.fg.attention.secondary',
            };
        }
        case 'positive': {
            return {
                backgroundColor: theme.palette.ds.bg.positive.tertiary,
                iconColor: 'ds.fg.positive.secondary',
            };
        }
    }
};

type AlertBorderInput = {
    theme: Theme;
    colorScheme: DesignSystemAlertColorScheme;
};

export const getBorderStyles = ({ theme, colorScheme }: AlertBorderInput) => {
    const borderColorType =
        colorScheme === 'neutral' ? 'secondary' : 'tertiary';

    return {
        border: `1px solid ${theme.palette.ds.border[colorScheme][borderColorType]}`,
    };
};

type AlertGapInput = {
    size: DesignSystemAlertSize;
    stacking: DesignSystemAlertStacking;
};

type AlertGapOutput = {
    columnGap: number;
    rowGap: number;
    contentGap: number;
    actionsGap: number;
    closeExtraGap: number;
    titleGap?: number;
    contentToActionsGap?: number;
};

const inlineGaps: Record<DesignSystemAlertSize, AlertGapOutput> = {
    xs: {
        columnGap: 1,
        rowGap: 0,
        contentGap: 1,
        actionsGap: 2,
        closeExtraGap: 0,
    },
    sm: {
        columnGap: 1,
        rowGap: 0,
        contentGap: 1,
        actionsGap: 2,
        closeExtraGap: 0,
    },
    md: {
        columnGap: 1,
        rowGap: 0,
        contentGap: 1,
        actionsGap: 2,
        closeExtraGap: 0,
    },
};

const stackedGaps: Record<DesignSystemAlertSize, AlertGapOutput> = {
    xs: {
        columnGap: 1,
        rowGap: 0,
        contentGap: 1,
        actionsGap: 2,
        closeExtraGap: 1,
        titleGap: 0.25,
        contentToActionsGap: 1,
    },
    sm: {
        columnGap: 1,
        rowGap: 0,
        contentGap: 1,
        actionsGap: 2,
        closeExtraGap: 2,
        titleGap: 0.5,
        contentToActionsGap: 1.5,
    },
    md: {
        columnGap: 1.5,
        rowGap: 0,
        contentGap: 1.5,
        actionsGap: 2,
        closeExtraGap: 1.5,
        titleGap: 0.5,
        contentToActionsGap: 1.5,
    },
};

export const getAlertGapStyles = ({
    size,
    stacking,
}: AlertGapInput): AlertGapOutput => {
    return stacking === 'inline' ? inlineGaps[size] : stackedGaps[size];
};
